// 활동지 v2 모델 — 단일 진실 공급원은 `.claude/skills/task-editor/SKILL.md`.
// JSON 키는 snake_case. id 필드 없음 — 위치는 배열 index 로 식별.
// Block shape: { description: TaskEditorDescription, action: ActionComponent | null }

// ─── 상수 ─────────────────────────────────────────────────────────────────

export const TASK_EDITOR_DESCRIPTION_TYPES = [
  "basic",
  "image",
  "label",
  "reference",
] as const;

export const TASK_EDITOR_REFERENCE_TYPES = [
  "automatic_thought",
  "coach_preset",
] as const;

export const TASK_EDITOR_ACTION_COMPONENT_TYPES = [
  "text_input",
  "button_default",
  "button_quiz",
  "button_branch",
  "audio",
] as const;

// block 단위 type — 미리보기 렌더 시 header/body 스타일 분기에 사용한다.
// "블록이 헤더인지 본문인지"는 block.type 이 단독으로 표현한다 (description 과 무관).
export const TASK_EDITOR_BLOCK_TYPES = ["header", "body"] as const;
export type TaskEditorBlockType = (typeof TASK_EDITOR_BLOCK_TYPES)[number];

// ─── 타입 ────────────────────────────────────────────────────────────────

export type TaskEditorDescriptionType =
  (typeof TASK_EDITOR_DESCRIPTION_TYPES)[number];
export type TaskEditorReferenceType =
  (typeof TASK_EDITOR_REFERENCE_TYPES)[number];
export type TaskEditorActionComponentType =
  (typeof TASK_EDITOR_ACTION_COMPONENT_TYPES)[number];

export interface TaskEditorButtonOption {
  text: string;
  is_correct?: boolean; // button_quiz
}

export interface TaskEditorTextInputField {
  label?: string;
  placeholder?: string;
  help_text?: string;
  // 기본 필수. true 일 때만 저장 (is_correct 와 동일 컨벤션) — 미리보기 진행
  // 게이팅에서 제외 (v1 의 optional 필드와 동일).
  optional?: boolean;
}

// 레이블형 description 의 레이블-텍스트 쌍. 최소 1개 (text_input fields 와 동일).
export interface TaskEditorLabelPair {
  label: string;
  text: string;
}

// 설명부 — 단일 객체 (필수). 텍스트(content)는 모든 유형 공통.
export type TaskEditorDescription =
  | { type: "basic"; content: string }
  | { type: "image"; content: string; image_url: string }
  | { type: "label"; content: string; pairs: TaskEditorLabelPair[] }
  | { type: "reference"; content: string; reference_type: "automatic_thought" }
  | {
      type: "reference";
      content: string;
      reference_type: "coach_preset";
      preset_text: string;
    };

export type TaskEditorActionComponent =
  | { type: "text_input"; fields: TaskEditorTextInputField[] }
  | {
      type: "button_default";
      options: TaskEditorButtonOption[];
      allow_multiple_selection: boolean;
    }
  | {
      type: "button_quiz";
      options: TaskEditorButtonOption[];
    }
  | { type: "button_branch"; options: TaskEditorButtonOption[] }
  | {
      // 명상 등에서 쓰는 오디오 플레이어. 미리보기/체험은 v1 명상 player 와 동일.
      // artwork 는 이미지와 동일하게 URL 또는 업로드 data URL.
      // audio_url / extra_audio_url 은 호스팅된 오디오 파일의 전체 URL.
      type: "audio";
      artwork: string;
      audio_url: string;
      // 선택 — 본 오디오가 끝난 뒤 반복 재생할 추가 오디오. 빈 값이면 키 생략.
      extra_audio_url?: string;
    };

// 블록은 단일 description 객체 + 선택적 action 객체. id 없음.
// type 은 미리보기에서 header/body 스타일 분기에 쓰이는 독립 입력값.
export interface TaskEditorBlock {
  type: TaskEditorBlockType;
  // 분기 태그 (선택) — 직전 button_branch 블록의 몇 번째 옵션(0-based) 분기에
  // 속하는지. 메인 흐름 블록은 키 없음. 배치 규칙: 분기 블록 바로 뒤 연속 구간,
  // 옵션 순서(비내림차순), 태그 블록은 button_branch action 금지 (깊이 1).
  branch_option?: number;
  description: TaskEditorDescription;
  action: TaskEditorActionComponent | null;
}

export interface TaskEditorState {
  task_id: string;
  title: string;
  // 필수값. sentinel: 미분류 = 9999, 툴 = 9998, 반복 = 9997. undefined 없음.
  set_number: number;
  is_key_activity?: boolean;
  // 같은 set_number 안에서의 정렬 키 (오름차순). 백엔드는 NOT NULL 이지만
  // 로컬 신규 활동지는 reorder/commit 전까지 미설정일 수 있어 optional.
  sort_order?: number;
  // 활동지 유형 — 부재 = 기본(basic). is_key_activity 처럼 non-default 만 보관.
  task_type?: TaskEditorTaskType;
  // 선행 활동지 task_id — task_type === "tool" 일 때만 의미.
  prerequisite_task_id?: string;
  // 연결 툴 활동지 task_id — task_type === "empty"(깡통) 일 때만 의미.
  linked_tool_task_id?: string;
  // 반복 주기(주 단위, 1|2) — task_type === "repeat" 일 때만 의미.
  repeat_interval_weeks?: TaskEditorRepeatIntervalWeeks;
  blocks: TaskEditorBlock[];
}

// 미분류 sentinel. 와이어(서버) 규약상 set_number 는 NOT NULL 이고 미분류 = 9999.
// 프론트 내부 모델은 "undefined = 미분류"를 쓰므로 경계에서만 9999 ↔ undefined 매핑한다.
export const UNCATEGORIZED_SET_NUMBER = 9999;

// 툴 sentinel — 툴 활동지는 세트 개념이 없어 전용 set_number 로 저장 (CONTRACT-list-meta.md §6).
export const TOOL_SET_NUMBER = 9998;

// 반복 sentinel — 반복 활동지도 세트 개념 없음. 값은 9997 이지만 섹션 표시 순서는
// [세트들 → 툴 → 반복 → 미분류] — taskEditorListUtils 의 섹션 정렬 rank 가 관리한다.
export const REPEAT_SET_NUMBER = 9997;

// 활동지 유형. 기본(basic) / 툴(tool, 선행 활동지 완료 시 열림) /
// 깡통(empty, 내부 content 없음) / 반복(repeat, 1·2주 주기 반복).
export const TASK_EDITOR_TASK_TYPES = [
  "basic",
  "tool",
  "empty",
  "repeat",
] as const;
export type TaskEditorTaskType = (typeof TASK_EDITOR_TASK_TYPES)[number];

export const TASK_TYPE_LABELS: Record<TaskEditorTaskType, string> = {
  basic: "기본",
  tool: "툴",
  empty: "깡통",
  repeat: "반복",
};

// 반복 주기 옵션 — 주 단위.
export const TASK_EDITOR_REPEAT_INTERVAL_WEEKS = [1, 2] as const;
export type TaskEditorRepeatIntervalWeeks =
  (typeof TASK_EDITOR_REPEAT_INTERVAL_WEEKS)[number];

// wire 값 → 내부 표현. basic·미지정·모르는 값 ⇒ undefined (부재 = basic 컨벤션).
export function normalizeTaskEditorTaskType(
  value: unknown
): Exclude<TaskEditorTaskType, "basic"> | undefined {
  return value === "tool" || value === "empty" || value === "repeat"
    ? value
    : undefined;
}

// wire 값 → 반복 주기. 1·2 외의 값은 버린다.
export function normalizeRepeatIntervalWeeks(
  value: unknown
): TaskEditorRepeatIntervalWeeks | undefined {
  return value === 1 || value === 2 ? value : undefined;
}

// task-meta/save(단건)·save-all(배치) 의 메타 페이로드 — 레코드당 전체 메타(full replace).
// 프론트 내부 모델과 동일한 snake_case. set_number 는 NOT NULL (미분류 = 9999), sort_order 는 정수 NOT NULL.
// (모델 레이어에 정의 — api 와 listUtils 가 함께 사용.)
export interface TaskMetaUpdate {
  task_id: string;
  title: string;
  set_number: number;
  is_key_activity: boolean;
  sort_order: number;
  // full replace 라 항상 명시 전송 (부재 시 백엔드가 basic 으로 덮어쓸 수 있음).
  task_type: TaskEditorTaskType;
  // tool 일 때만 포함.
  prerequisite_task_id?: string;
  // empty(깡통) 일 때만 포함.
  linked_tool_task_id?: string;
  // repeat(반복) 일 때만 포함 — 주기(주 단위, 1|2).
  repeat_interval_weeks?: TaskEditorRepeatIntervalWeeks;
}

// 분기 드릴다운 스코프 — 어떤 분기 블록(blockIndex)의 어떤 옵션(optionIndex)
// 세그먼트를 편집/미리보기 중인지. 워크스페이스·미리보기가 공유 (패널이 소유).
export interface TaskEditorBranchScope {
  blockIndex: number;
  optionIndex: number;
}

// 편집 UI 의 삭제 대상 — 블록 전체 또는 액션 (description 은 필수라 삭제 불가).
export type TaskEditorDeleteTarget =
  | { type: "block"; blockIndex: number }
  | { type: "action"; blockIndex: number };

export type TaskEditorBlockDropPreview = { insertionIndex: number };

// ─── 라벨 ─────────────────────────────────────────────────────────────────

export function getTaskEditorDescriptionTypeLabel(
  type: TaskEditorDescriptionType
) {
  switch (type) {
    case "basic":
      return "기본";
    case "image":
      return "이미지";
    case "label":
      return "레이블";
    case "reference":
      return "불러오기";
    default:
      return type;
  }
}

export function getTaskEditorReferenceTypeLabel(type: TaskEditorReferenceType) {
  switch (type) {
    case "automatic_thought":
      return "자동적 사고";
    case "coach_preset":
      return "코치 프리셋";
    default:
      return type;
  }
}

export function getTaskEditorComponentTypeLabel(
  type: TaskEditorActionComponentType
) {
  switch (type) {
    case "text_input":
      return "주관식";
    case "button_default":
      return "객관식";
    case "button_quiz":
      return "객관식 (정답형)";
    case "button_branch":
      return "객관식 (분기형)";
    case "audio":
      return "오디오";
    default:
      return type;
  }
}

// button_quiz 옵션의 고정 글자 라벨(A, B, C…). index 기반 표현이며 text 에 저장하지 않는다.
// 26개 초과(범위 밖)는 표시 라벨이 없으므로 빈 문자열 — 운영상 도달하지 않는 경계.
export function getTaskEditorQuizOptionLabel(index: number): string {
  if (index < 0 || index >= 26) return "";
  return String.fromCharCode(65 + index);
}

// ─── 생성자 ───────────────────────────────────────────────────────────────

export function createTaskEditorButtonOption(
  value?: Partial<TaskEditorButtonOption>
): TaskEditorButtonOption {
  return {
    text: typeof value?.text === "string" ? value.text : "",
    ...(value?.is_correct === true ? { is_correct: true } : {}),
  };
}

export function createTaskEditorTextInputField(): TaskEditorTextInputField {
  return {};
}

export function createTaskEditorLabelPair(): TaskEditorLabelPair {
  return { label: "", text: "" };
}

export function createTaskEditorDescription(
  type: TaskEditorDescriptionType = "basic"
): TaskEditorDescription {
  switch (type) {
    case "basic":
      return { type: "basic", content: "" };
    case "image":
      return { type: "image", content: "", image_url: "" };
    case "label":
      return {
        type: "label",
        content: "",
        pairs: [createTaskEditorLabelPair()],
      };
    case "reference":
      return {
        type: "reference",
        content: "",
        reference_type: "automatic_thought",
      };
  }
}

export function createTaskEditorActionComponent(
  type: TaskEditorActionComponentType = "text_input"
): TaskEditorActionComponent {
  switch (type) {
    case "text_input":
      return { type: "text_input", fields: [createTaskEditorTextInputField()] };
    case "button_default":
      return {
        type: "button_default",
        allow_multiple_selection: true,
        options: [
          createTaskEditorButtonOption(),
          createTaskEditorButtonOption(),
        ],
      };
    case "button_quiz":
      return {
        type: "button_quiz",
        options: [
          createTaskEditorButtonOption(),
          createTaskEditorButtonOption(),
        ],
      };
    case "button_branch":
      return {
        type: "button_branch",
        options: [
          createTaskEditorButtonOption(),
          createTaskEditorButtonOption(),
        ],
      };
    case "audio":
      return { type: "audio", artwork: "", audio_url: "" };
  }
}

export function createTaskEditorBlock(
  type: TaskEditorBlockType = "body"
): TaskEditorBlock {
  return { type, description: createTaskEditorDescription(), action: null };
}

// ─── reorder helpers ─────────────────────────────────────────────────────

function reorderArray<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  if (
    fromIndex < 0 ||
    fromIndex >= arr.length ||
    toIndex < 0 ||
    toIndex >= arr.length ||
    fromIndex === toIndex
  ) {
    return arr;
  }
  const next = [...arr];
  const [target] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, target);
  return next;
}

export const reorderBlocks = reorderArray;

export function moveBlock(
  blocks: TaskEditorBlock[],
  index: number,
  direction: "up" | "down"
) {
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  return reorderArray(blocks, index, targetIndex);
}

// ─── 이미지 유틸 ──────────────────────────────────────────────────────────

export function convertTaskEditorImageFileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const result = fileReader.result;
      if (typeof result !== "string") {
        reject(new Error("이미지 파일을 읽지 못했습니다."));
        return;
      }
      resolve(result);
    };
    fileReader.onerror = () =>
      reject(new Error("이미지 파일을 읽지 못했습니다."));
    fileReader.readAsDataURL(file);
  });
}

export function isTaskEditorLocalImageDataUrl(value: string) {
  return value.trim().startsWith("data:image/");
}

// ─── 옵션 헬퍼 ───────────────────────────────────────────────────────────

export function normalizeTaskEditorButtonOption(
  option: unknown
): TaskEditorButtonOption {
  if (typeof option === "string") {
    return createTaskEditorButtonOption({ text: option });
  }
  if (!option || typeof option !== "object") {
    return createTaskEditorButtonOption();
  }
  const c = option as Record<string, unknown>;
  // 계약에 없는 필드(show_from/show_to/restart_from/hide_from 등)는 복사하지
  // 않는 것으로 strip — 분기는 블록 레벨 branch_option 태그로 표현한다.
  return createTaskEditorButtonOption({
    text: typeof c.text === "string" ? c.text : "",
    ...(c.is_correct === true ? { is_correct: true } : {}),
  });
}

export function getTaskEditorButtonOptions(
  options: TaskEditorButtonOption[] | undefined
) {
  if (!Array.isArray(options) || options.length === 0) {
    return [createTaskEditorButtonOption(), createTaskEditorButtonOption()];
  }
  return options.map((o) => normalizeTaskEditorButtonOption(o));
}

// ─── 분기 그룹 파생 (button_branch + branch_option 태그) ─────────────────
// flat blocks 배열에서 분기 구조를 파생하는 단일 출처. 규칙:
//   1. branch_option 태그 블록은 button_branch 블록 바로 뒤 연속 구간에만
//   2. 구간 내 태그 값은 비내림차순 (옵션 순서대로 세그먼트 배치)
//   3. 0 <= branch_option < options.length
// 규칙을 위반한 태그 블록(고아·범위 밖·순서 역전)은 방어적으로 main 취급해
// 렌더가 깨지지 않게 한다 (구조 검증·교정은 BE 책임).

export interface TaskEditorBranchSegment {
  optionIndex: number;
  start: number; // 0-based inclusive
  end: number; // 0-based exclusive
}

export type TaskEditorBlockGroup =
  | { kind: "main"; index: number }
  | { kind: "branch"; index: number; segments: TaskEditorBranchSegment[] };

function isValidBranchTag(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

export function buildTaskEditorBlockGroups(
  blocks: TaskEditorBlock[]
): TaskEditorBlockGroup[] {
  const groups: TaskEditorBlockGroup[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (
      block.action?.type === "button_branch" &&
      !isValidBranchTag(block.branch_option)
    ) {
      const optionCount = block.action.options.length;
      const segments: TaskEditorBranchSegment[] = [];
      let j = i + 1;
      let lastTag = -1;
      while (j < blocks.length) {
        const tag = blocks[j].branch_option;
        if (!isValidBranchTag(tag) || tag < lastTag || tag >= optionCount) {
          break;
        }
        const lastSegment = segments[segments.length - 1];
        if (lastSegment && lastSegment.optionIndex === tag) {
          lastSegment.end = j + 1;
        } else {
          segments.push({ optionIndex: tag, start: j, end: j + 1 });
        }
        lastTag = tag;
        j += 1;
      }
      groups.push({ kind: "branch", index: i, segments });
      i = j;
    } else {
      groups.push({ kind: "main", index: i });
      i += 1;
    }
  }
  return groups;
}

// 분기 그룹(분기 블록 + 세그먼트 run)의 0-based [start, end) span.
// 분기 블록이 아니면 null. 세그먼트가 없으면 end = index + 1.
export function getTaskEditorBranchRunSpan(
  blocks: TaskEditorBlock[],
  branchBlockIndex: number
): { start: number; end: number } | null {
  const group = buildTaskEditorBlockGroups(blocks).find(
    (g): g is Extract<TaskEditorBlockGroup, { kind: "branch" }> =>
      g.kind === "branch" && g.index === branchBlockIndex
  );
  if (!group) return null;
  const lastSegment = group.segments[group.segments.length - 1];
  return {
    start: branchBlockIndex,
    end: lastSegment ? lastSegment.end : branchBlockIndex + 1,
  };
}

// 특정 옵션 세그먼트의 0-based [start, end) span. 세그먼트가 없으면 삽입
// 위치(더 큰 옵션 세그먼트 앞, 없으면 run 끝)를 빈 구간 [pos, pos) 으로 반환.
export function getTaskEditorBranchSegmentSpan(
  blocks: TaskEditorBlock[],
  branchBlockIndex: number,
  optionIndex: number
): { start: number; end: number } | null {
  const block = blocks[branchBlockIndex];
  if (block?.action?.type !== "button_branch") return null;
  if (optionIndex < 0 || optionIndex >= block.action.options.length) {
    return null;
  }
  const group = buildTaskEditorBlockGroups(blocks).find(
    (g): g is Extract<TaskEditorBlockGroup, { kind: "branch" }> =>
      g.kind === "branch" && g.index === branchBlockIndex
  );
  if (!group) return null; // 분기 블록 자신이 태그된 비정상 상태 — 손대지 않음
  let insertion = branchBlockIndex + 1;
  for (const segment of group.segments) {
    if (segment.optionIndex === optionIndex) {
      return { start: segment.start, end: segment.end };
    }
    if (segment.optionIndex > optionIndex) break;
    insertion = segment.end;
  }
  return { start: insertion, end: insertion };
}

export function getTaskEditorBranchSegmentBlocks(
  blocks: TaskEditorBlock[],
  branchBlockIndex: number,
  optionIndex: number
): TaskEditorBlock[] {
  const span = getTaskEditorBranchSegmentSpan(
    blocks,
    branchBlockIndex,
    optionIndex
  );
  return span ? blocks.slice(span.start, span.end) : [];
}

// 사람이 보는 블록 라벨. 메인 흐름(태그 없는 블록)만 세어 "블록 K", 세그먼트
// 블록은 "블록 K · 옵션 M · J" (K=분기 블록의 메인 번호, M/J 는 1-based).
// JSON/내부 index 의 raw 위치와 분리된 표시 전용 번호다.
export function getTaskEditorBlockDisplayLabels(
  blocks: TaskEditorBlock[]
): string[] {
  const labels = new Array<string>(blocks.length).fill("");
  let mainNumber = 0;
  for (const group of buildTaskEditorBlockGroups(blocks)) {
    mainNumber += 1;
    labels[group.index] = `블록 ${mainNumber}`;
    if (group.kind !== "branch") continue;
    for (const segment of group.segments) {
      for (let i = segment.start; i < segment.end; i += 1) {
        labels[i] =
          `블록 ${mainNumber} · 옵션 ${segment.optionIndex + 1} · ${i - segment.start + 1}`;
      }
    }
  }
  return labels;
}

// 분기 구조 시그니처 — 미리보기 세션 리셋 트리거용. 블록 수·분기 위치·옵션
// 수·태그 배열이 바뀌면 달라지고, 텍스트 등 내용 편집에는 불변.
export function getTaskEditorBranchSignature(
  blocks: TaskEditorBlock[]
): string {
  return blocks
    .map((b) =>
      b.action?.type === "button_branch"
        ? `b${b.action.options.length}`
        : isValidBranchTag(b.branch_option)
          ? `t${b.branch_option}`
          : "."
    )
    .join("|");
}

// ─── 그룹 단위 재정렬 (루트 편집 뷰) ─────────────────────────────────────
// 분기 블록 드래그는 세그먼트 run 과 한 묶음으로 움직인다.

function getTaskEditorGroupSpanAtHead(
  blocks: TaskEditorBlock[],
  headIndex: number
): { start: number; end: number } | null {
  const group = buildTaskEditorBlockGroups(blocks).find(
    (g) => g.index === headIndex
  );
  if (!group) return null;
  const lastSegment =
    group.kind === "branch"
      ? group.segments[group.segments.length - 1]
      : undefined;
  return {
    start: group.index,
    end: lastSegment ? lastSegment.end : group.index + 1,
  };
}

// preview.insertionIndex(블록 사이 원시 위치)가 다른 그룹 내부를 가리키면 그
// 그룹 끝으로 스냅한 뒤, 이동 후 그룹 head 가 놓일 0-based index 를 반환.
// no-op(제자리·자기 그룹 내부)이거나 source 가 그룹 head 가 아니면 null.
export function getTaskEditorGroupReorderTarget(
  blocks: TaskEditorBlock[],
  sourceBlockIndex: number,
  preview: TaskEditorBlockDropPreview
): number | null {
  const groups = buildTaskEditorBlockGroups(blocks);
  const spans = groups.map((g) => {
    const lastSegment =
      g.kind === "branch" ? g.segments[g.segments.length - 1] : undefined;
    return { start: g.index, end: lastSegment ? lastSegment.end : g.index + 1 };
  });
  const source = spans.find((s) => s.start === sourceBlockIndex);
  if (!source) return null; // 세그먼트 블록은 루트에서 드래그 비허용
  let insertion = Math.max(0, Math.min(preview.insertionIndex, blocks.length));
  for (const span of spans) {
    if (insertion > span.start && insertion < span.end) {
      insertion = span.end;
      break;
    }
  }
  if (insertion >= source.start && insertion <= source.end) return null;
  const size = source.end - source.start;
  const target = insertion > source.end ? insertion - size : insertion;
  return target === source.start ? null : target;
}

export function reorderTaskEditorBlockGroup(
  blocks: TaskEditorBlock[],
  sourceBlockIndex: number,
  targetIndex: number
): TaskEditorBlock[] {
  const span = getTaskEditorGroupSpanAtHead(blocks, sourceBlockIndex);
  if (!span) return blocks;
  const next = [...blocks];
  const moved = next.splice(span.start, span.end - span.start);
  const clamped = Math.max(0, Math.min(targetIndex, next.length));
  next.splice(clamped, 0, ...moved);
  return next;
}

// ─── 드롭존 인덱스 ────────────────────────────────────────────────────────

export function getTaskEditorBlockReorderIndex(
  blocks: TaskEditorBlock[],
  sourceBlockIndex: number,
  preview: TaskEditorBlockDropPreview
) {
  if (sourceBlockIndex < 0 || sourceBlockIndex >= blocks.length) {
    return null;
  }
  const nextIndex =
    preview.insertionIndex > sourceBlockIndex
      ? preview.insertionIndex - 1
      : preview.insertionIndex;
  return Math.max(0, Math.min(blocks.length - 1, nextIndex));
}

export function isSameTaskEditorBlockDropPreview(
  currentPreview: TaskEditorBlockDropPreview | null,
  nextPreview: TaskEditorBlockDropPreview
) {
  return currentPreview?.insertionIndex === nextPreview.insertionIndex;
}

// ─── 단축키 ───────────────────────────────────────────────────────────────

export const isTaskEditorUndoShortcut = (event: KeyboardEvent) => {
  if (event.altKey || event.shiftKey || event.isComposing) {
    return false;
  }
  return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z";
};

// ─── Shape 정규화 ────────────────────────────────────────────────────────
// 현행 contract 의 방어적 정규화. cloneTaskEditorState 가 진입점에서 호출.
// null/누락 필드는 기본값으로 채우고, 미지 description type 은 기본형(basic)
// 으로 폴백한다. 변환·마이그레이션 없음 — 입출력 모두 현행 스키마.

function pickOptionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  return value.length > 0 ? value : undefined;
}

function normalizeTextInputField(value: unknown): TaskEditorTextInputField {
  if (!value || typeof value !== "object") {
    return createTaskEditorTextInputField();
  }
  const f = value as Record<string, unknown>;
  const result: TaskEditorTextInputField = {};
  const label = pickOptionalString(f.label);
  if (label !== undefined) result.label = label;
  const placeholder = pickOptionalString(f.placeholder);
  if (placeholder !== undefined) result.placeholder = placeholder;
  const help_text = pickOptionalString(f.help_text);
  if (help_text !== undefined) result.help_text = help_text;
  // 기본 필수 — optional: true 일 때만 보존.
  if (f.optional === true) result.optional = true;
  return result;
}

// raw component 를 action 으로 정규화. action 형식이 아니면 null.
function normalizeAction(value: unknown): TaskEditorActionComponent | null {
  if (!value || typeof value !== "object") return null;
  const c = value as Record<string, unknown>;

  switch (c.type) {
    case "text_input": {
      const fields = Array.isArray(c.fields)
        ? c.fields.map((f) => normalizeTextInputField(f))
        : [createTaskEditorTextInputField()];
      return { type: "text_input", fields };
    }
    case "button_default":
      return {
        type: "button_default",
        allow_multiple_selection: c.allow_multiple_selection !== false,
        options: Array.isArray(c.options)
          ? c.options.map((o) => normalizeTaskEditorButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    case "button_quiz":
      return {
        type: "button_quiz",
        options: Array.isArray(c.options)
          ? c.options.map((o) => normalizeTaskEditorButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    case "button_branch":
      return {
        type: "button_branch",
        options: Array.isArray(c.options)
          ? c.options.map((o) => normalizeTaskEditorButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    case "audio": {
      const extra =
        typeof c.extra_audio_url === "string" ? c.extra_audio_url : "";
      return {
        type: "audio",
        artwork: typeof c.artwork === "string" ? c.artwork : "",
        audio_url: typeof c.audio_url === "string" ? c.audio_url : "",
        ...(extra ? { extra_audio_url: extra } : {}),
      };
    }
    default:
      return null;
  }
}

// 레이블-텍스트 쌍 정규화 — label/text 가 문자열이 아니면 빈 문자열.
function normalizeLabelPair(value: unknown): TaskEditorLabelPair {
  if (!value || typeof value !== "object") return createTaskEditorLabelPair();
  const p = value as Record<string, unknown>;
  return {
    label: typeof p.label === "string" ? p.label : "",
    text: typeof p.text === "string" ? p.text : "",
  };
}

// 단일 description 객체 정규화. 미지 type·비객체는 null (블록 레벨에서 폴백).
function normalizeDescription(value: unknown): TaskEditorDescription | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const d = value as Record<string, unknown>;
  const content = typeof d.content === "string" ? d.content : "";
  switch (d.type) {
    case "basic":
      return { type: "basic", content };
    case "image":
      return {
        type: "image",
        content,
        image_url: typeof d.image_url === "string" ? d.image_url : "",
      };
    case "label": {
      // pairs 각 항목의 label/text 비문자열 → "". 비배열·빈 배열은 기본 쌍 1개.
      const pairs = Array.isArray(d.pairs)
        ? d.pairs.map(normalizeLabelPair)
        : [];
      return {
        type: "label",
        content,
        pairs: pairs.length > 0 ? pairs : [createTaskEditorLabelPair()],
      };
    }
    case "reference":
      return d.reference_type === "coach_preset"
        ? {
            type: "reference",
            content,
            reference_type: "coach_preset",
            preset_text: typeof d.preset_text === "string" ? d.preset_text : "",
          }
        : { type: "reference", content, reference_type: "automatic_thought" };
    default:
      return null;
  }
}

// 분기 태그 보존 — 단 태그 블록이 button_branch action 을 가지면 (깊이 1
// 위반) 태그를 버려 메인 분기 블록으로 살린다.
function normalizeBranchOption(
  raw: unknown,
  action: TaskEditorActionComponent | null
): number | undefined {
  return typeof raw === "number" &&
    Number.isInteger(raw) &&
    raw >= 0 &&
    action?.type !== "button_branch"
    ? raw
    : undefined;
}

function normalizeBlock(value: unknown): TaskEditorBlock | null {
  if (!value || typeof value !== "object") return null;
  const b = value as Record<string, unknown>;
  // description 은 필수 — 미지 type·형식 위반은 기본형(basic) 으로 폴백.
  const description =
    normalizeDescription(b.description) ?? createTaskEditorDescription();
  const action = normalizeAction(b.action);
  const branch_option = normalizeBranchOption(b.branch_option, action);
  return {
    type: b.type === "header" ? "header" : "body",
    ...(branch_option !== undefined ? { branch_option } : {}),
    description,
    action,
  };
}

export function normalizeTaskEditorStateShape(value: unknown): TaskEditorState {
  if (!value || typeof value !== "object") {
    return {
      task_id: "",
      title: "",
      set_number: UNCATEGORIZED_SET_NUMBER,
      // 새/빈 활동지의 첫 블록은 헤더 기본값.
      blocks: [createTaskEditorBlock("header")],
    };
  }
  const s = value as Record<string, unknown>;
  const task_id = typeof s.task_id === "string" ? s.task_id : "";
  const title = typeof s.title === "string" ? s.title : "";
  // set_number 는 필수값 — 없거나 숫자가 아니면 미분류(9999)로 기본값.
  const set_number =
    typeof s.set_number === "number" && Number.isFinite(s.set_number)
      ? s.set_number
      : UNCATEGORIZED_SET_NUMBER;
  const is_key_activity = s.is_key_activity === true ? true : undefined;
  const task_type = normalizeTaskEditorTaskType(s.task_type);
  const prerequisite_task_id =
    typeof s.prerequisite_task_id === "string" && s.prerequisite_task_id
      ? s.prerequisite_task_id
      : undefined;
  const linked_tool_task_id =
    typeof s.linked_tool_task_id === "string" && s.linked_tool_task_id
      ? s.linked_tool_task_id
      : undefined;
  const repeat_interval_weeks = normalizeRepeatIntervalWeeks(
    s.repeat_interval_weeks
  );
  const rawBlocks = Array.isArray(s.blocks) ? s.blocks : [];
  const blocks = rawBlocks
    .map((b) => normalizeBlock(b))
    .filter((b): b is TaskEditorBlock => b !== null);
  return {
    task_id,
    title,
    set_number,
    ...(is_key_activity !== undefined ? { is_key_activity } : {}),
    ...(task_type !== undefined ? { task_type } : {}),
    ...(prerequisite_task_id !== undefined ? { prerequisite_task_id } : {}),
    ...(linked_tool_task_id !== undefined ? { linked_tool_task_id } : {}),
    ...(repeat_interval_weeks !== undefined ? { repeat_interval_weeks } : {}),
    // 유효 블록이 없으면 헤더 블록 1개로 폴백 (새/빈 활동지 첫 블록 = 헤더).
    blocks: blocks.length > 0 ? blocks : [createTaskEditorBlock("header")],
  };
}
